import React from 'react';
import { Drawer, Timeline, Spin, Empty, Tag, Avatar, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useGetAppointmentAuditLogQuery } from '../../services/appointments';
import { useGetDogByIDQuery } from '../../services/dogsApi';
import { formatToEastern } from '../../helpers/helpers';

const DogChip = ({ id, onOpenDog }) => {
  const { data, isFetching } = useGetDogByIDQuery(id, { skip: !id });
  const dog = data?.data;
  const label = isFetching
    ? '…'
    : dog?.dog_name || `Dog ${String(id).slice(-6)}`;
  return (
    <button
      type="button"
      className="text-blue-600 hover:underline mr-1"
      onClick={e => {
        e.stopPropagation();
        if (onOpenDog) onOpenDog(id);
      }}
    >
      {label}
    </button>
  );
};

const DogList = ({ ids, onOpenDog }) => {
  if (!Array.isArray(ids) || ids.length === 0) return <span>none</span>;
  return (
    <span>
      [{ids.length}]{' '}
      {ids.map((id, i) => (
        <React.Fragment key={`${id}-${i}`}>
          <DogChip id={id} onOpenDog={onOpenDog} />
          {i < ids.length - 1 && <span className="text-gray-400">, </span>}
        </React.Fragment>
      ))}
    </span>
  );
};

const FIELD_LABELS = {
  appointment_start_time: 'Start time',
  appointment_end_time: 'End time',
  appointment_dates: 'Daycare dates',
  service_id: 'Service',
  dogs: 'Dogs',
  status: 'Status',
  check_in_time: 'Check-in time',
  check_out_time: 'Check-out time',
  priceAtAppoTime: 'Price at booking',
  paidAmount: 'Paid amount',
  netTotal: 'Total',
  discount: 'Discount',
  tax: 'Tax',
  chargePerDay: 'Charge per day',
  extraCharges: 'Extra charges',
  latePickupFee: 'Late pickup fee',
  isPaid: 'Paid',
  paymentMethod: 'Payment method',
  invoice_sent_at: 'Invoice sent at',
  invoice_dirty: 'Invoice out of date',
  notes: 'Notes',
  locker_number: 'Locker',
};

const ACTION_META = {
  create: { label: 'Created', color: 'green' },
  update: { label: 'Updated', color: 'blue' },
  cancel: { label: 'Cancelled', color: 'red' },
  check_in: { label: 'Checked in', color: 'cyan' },
  check_out: { label: 'Checked out', color: 'geekblue' },
  status_change: { label: 'Status changed', color: 'purple' },
  invoice_sent: { label: 'Invoice sent', color: 'gold' },
  locker_update: { label: 'Locker updated', color: 'magenta' },
};

const MONEY_FIELDS = new Set([
  'netTotal',
  'paidAmount',
  'priceAtAppoTime',
  'discount',
  'chargePerDay',
  'latePickupFee',
]);
const DATE_FIELDS = new Set([
  'appointment_start_time',
  'appointment_end_time',
  'check_in_time',
  'check_out_time',
  'invoice_sent_at',
]);

const formatValue = (field, value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (DATE_FIELDS.has(field)) {
    return `${formatToEastern(value, 'DD-MMM-YYYY h:mm A')} ET`;
  }
  if (MONEY_FIELDS.has(field) && typeof value === 'number') {
    return `$${value.toFixed(2)}`;
  }
  if (field === 'extraCharges' && Array.isArray(value)) {
    if (value.length === 0) return 'none';
    return value
      .map(
        c => `${c?.label || 'charge'}: $${Number(c?.amount || 0).toFixed(2)}`
      )
      .join(', ');
  }
  if (Array.isArray(value)) {
    return value.length === 0
      ? 'none'
      : `[${value.length}] ${value.join(', ')}`;
  }
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const ChangeRow = ({ change, onOpenDog }) => {
  const label = FIELD_LABELS[change.field] || change.field;
  if (change.field === 'dogs') {
    return (
      <div className="text-xs leading-5">
        <span className="text-gray-500">{label}: </span>
        <span className="line-through text-gray-400">
          <DogList ids={change.from} onOpenDog={onOpenDog} />
        </span>
        <span className="text-gray-400"> → </span>
        <span className="font-medium text-gray-800">
          <DogList ids={change.to} onOpenDog={onOpenDog} />
        </span>
      </div>
    );
  }
  return (
    <div className="text-xs leading-5">
      <span className="text-gray-500">{label}: </span>
      <span className="line-through text-gray-400">
        {formatValue(change.field, change.from)}
      </span>
      <span className="text-gray-400"> → </span>
      <span className="font-medium text-gray-800">
        {formatValue(change.field, change.to)}
      </span>
    </div>
  );
};

const EntryCard = ({ entry, onOpenDog }) => {
  const meta = ACTION_META[entry.action] || {
    label: entry.action,
    color: 'default',
  };
  const actorName =
    entry.actor?.full_name ||
    entry.actor?.username ||
    entry.actor?.email ||
    'Unknown';
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Tag color={meta.color} className="!m-0">
          {meta.label}
        </Tag>
        {entry.actor_is_super_admin && (
          <Tag color="orange" className="!m-0">
            Admin
          </Tag>
        )}
        <Tooltip title={dayjs(entry.createdAt).format('YYYY-MM-DD HH:mm:ss')}>
          <span className="text-xs text-gray-500">
            {dayjs(entry.createdAt).fromNow
              ? dayjs(entry.createdAt).format('MMM D, YYYY h:mm A')
              : entry.createdAt}
          </span>
        </Tooltip>
      </div>
      <div className="flex items-center gap-2 mb-2">
        <Avatar size={20} src={entry.actor?.avatar}>
          {actorName.charAt(0).toUpperCase()}
        </Avatar>
        <span className="text-sm text-gray-700">{actorName}</span>
      </div>
      {entry.changes && entry.changes.length > 0 && (
        <div className="bg-gray-50 rounded p-2 space-y-1">
          {entry.changes.map((c, i) => (
            <ChangeRow
              key={`${c.field}-${i}`}
              change={c}
              onOpenDog={onOpenDog}
            />
          ))}
        </div>
      )}
      {entry.action === 'create' &&
        (!entry.changes || entry.changes.length === 0) && (
          <div className="text-xs text-gray-500 italic">
            Appointment created.
          </div>
        )}
    </div>
  );
};

const AppointmentHistoryDrawer = ({
  isOpen,
  onClose,
  appointmentId,
  onOpenDog,
  zIndex,
}) => {
  const { data, isFetching, isError, error } = useGetAppointmentAuditLogQuery(
    { id: appointmentId, page: 0, limit: 100 },
    { skip: !appointmentId || !isOpen, refetchOnMountOrArgChange: true }
  );

  const entries = data?.data?.entries || [];

  return (
    <Drawer
      title="Appointment History"
      placement="right"
      width={480}
      open={isOpen}
      onClose={onClose}
      zIndex={zIndex}
      destroyOnClose
    >
      {isFetching ? (
        <div className="flex justify-center py-10">
          <Spin />
        </div>
      ) : isError ? (
        <div className="text-sm text-red-600">
          Failed to load history: {error?.data?.msg || 'Unknown error'}
        </div>
      ) : entries.length === 0 ? (
        <Empty description="No history yet" />
      ) : (
        <Timeline
          items={entries.map(entry => ({
            key: entry._id,
            color: ACTION_META[entry.action]?.color || 'gray',
            children: <EntryCard entry={entry} onOpenDog={onOpenDog} />,
          }))}
        />
      )}
    </Drawer>
  );
};

export default AppointmentHistoryDrawer;
